import {
  ChangeEvent,
  ReactElement,
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
} from 'react'
import { breakpoints } from '../../../../theme/breakpoints'
import { defaultTheme } from '../../../../theme/index'
import styled from 'styled-components'
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import { I18n, Translate2 } from 'common'
import { observer } from 'mobx-react-lite'
import { TablePicker } from './TablePicker'
import {
  TableColumnOption,
  TableColumn,
  VehicleDropdownProps,
  VehicleDropdownOnChangeProps,
} from '../interfaces'
import { VehicleDropdownKeys } from '../store/VehicleDropdownKeys'

const MAX_FILTERED_RESULTS_TO_DISPLAY = 10

const flattenOptions = (cols: Array<TableColumn>): Array<TableColumn> => {
  const options: TableColumnOption[] = []
  cols.forEach((col) => {
    if (Array.isArray(col?.options))
      col?.options.forEach((option) => {
        options.push({
          id: option.id,
          value: option.value,
          typeId: col.id,
          typeName: col.header,
          ...(option.regionIds && { regionIds: option.regionIds }),
        })
      })
  })
  return [{ id: 999999, header: '', options }]
}

export const VehicleDropdown = observer(
  (props: VehicleDropdownProps): ReactElement => {
    const {
      label,
      selection,
      active,
      disabled,
      onChange,
      onClick,
      onCancel,
      options: availableOptions,
      enableSearch,
      disableSearch,
    } = props

    const onSelectItem = (newSelection: VehicleDropdownOnChangeProps) => {
      if (!newSelection || !newSelection.selection) {
        return
      }
      const label =
        newSelection.selection.value === 'All Engine'
          ? Translate2('allEngines')
          : newSelection.selection.value
      setFilterTerm(label)
      onChange(newSelection)
    }

    const [filteredOptions, setFilteredOptions] = useState(
      new Array<TableColumn>()
    )
    const [filterTerm, setFilterTerm] = useState('')
    const inputSearchRef = useRef<HTMLInputElement>(null)
    const fieldRef = useRef<HTMLInputElement>(null)
    const tablePickerRef = useRef<HTMLDivElement>(null)
    const flatOptions = flattenOptions(availableOptions)
    const [open, setOpen] = useState(active)

    useEffect(() => {
      setOpen(active)
    }, [active])

    useEffect(() => {
      if (selection === undefined) {
        setFilterTerm('')
      }
    }, [selection])

    useEffect(() => {
      if (selection?.value !== undefined) {
        const label =
          selection?.value === 'All Engine'
            ? Translate2('allEngines')
            : selection?.value
        setFilterTerm(label)
      }
    }, [selection?.value, active])

    useEffect(() => {
      if (active) {
        inputSearchRef?.current?.focus()
      }
    }, [active, filterTerm, label])

    useEffect(() => {
      setFilteredOptions(availableOptions)
    }, [availableOptions])

    const handleSearchInput = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e?.target?.value
      setFilterTerm(value)
      if (value === '') {
        setFilteredOptions(availableOptions)
        return
      }

      const filtered = flatOptions.map((item) => {
        return {
          id: item.id,
          header: item.header,
          options: item.options.filter((option) =>
            String(option.value).toLowerCase().includes(value?.toLowerCase())
          ),
        }
      })

      if (label !== 'year') {
        filtered[0].options = filtered[0]?.options?.slice(
          0,
          MAX_FILTERED_RESULTS_TO_DISPLAY
        )
      }

      setFilteredOptions(filtered)
    }

    const handleOnClick = () => {
      setOpen(true)
      if (!disabled) {
        onClick()
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
      const typeId = filteredOptions[0]?.id
      const typeName = filteredOptions[0]?.header
      const firstOption = filteredOptions[0]?.options[0]
      const newSelection = {
        selection: firstOption,
        selectionType: {
          id: firstOption?.typeId ?? typeId,
          value: firstOption?.typeName ?? typeName,
        },
      }
      onSelectItem(newSelection)
      if (e.keyCode === VehicleDropdownKeys.SEARCH_KEYCODE) {
        tablePickerRef?.current?.focus({ preventScroll: true })
      }
    }

    const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.keyCode === VehicleDropdownKeys.SEARCH_KEYCODE) {
        tablePickerRef?.current?.focus({ preventScroll: true })
      }
    }

    const handleKeyType = (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === VehicleDropdownKeys.SEARCH_KEY_TAB) {
        e.preventDefault()
        handleKeyDown(e)
      }
      if (e.key === VehicleDropdownKeys.SEARCH_KEY_ENTER) {
        handleKeyDown(e)
      }
    }

    const handleOnCancel = () => {
      setOpen(false)
      onCancel?.()
    }

    return (
      <>
        <Element>
          <Label>
            <I18n name={label} />
          </Label>
          <FieldButtonWrapper active={active}>
            {enableSearch && (
              <SearchContainer
                disabled={disableSearch}
                tabIndex={0}
                ref={fieldRef}
              >
                <SearchInput
                  type="text"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleSearchInput(e)
                  }}
                  maxLength={label === 'year' ? 4 : undefined}
                  onFocus={handleOnClick}
                  value={filterTerm || ''}
                  ref={inputSearchRef}
                  onKeyUp={handleKeyUp}
                  onKeyDown={handleKeyType}
                  disabled={disableSearch}
                />
                <Arrow>
                  {open ? (
                    <ArrowDropUp onClick={() => setOpen(false)} />
                  ) : (
                    <ArrowDropDown
                      onClick={() => setOpen(true)}
                      htmlColor={
                        disabled
                          ? defaultTheme.colors.disabledButtonText
                          : defaultTheme.colors.black
                      }
                    />
                  )}
                </Arrow>
              </SearchContainer>
            )}
          </FieldButtonWrapper>
        </Element>

        <TablePicker
          label={label}
          parentRef={fieldRef}
          isActive={open}
          onSelectItem={onSelectItem}
          columns={filteredOptions}
          selectedOption={selection}
          onCancel={handleOnCancel}
          tableRef={tablePickerRef}
        />
      </>
    )
  }
)

VehicleDropdown.displayName = 'VehicleDropdown'

const Element = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    width: 100%;
  }
`

const Label = styled.div`
  font-weight: bold;
  font-family: Roboto;
  font-size: 14px;
  margin: 4px 0px;
  text-align: left;
`

const FieldButtonWrapper = styled.span<{ active: boolean }>`
  flex-grow: 1;
  border-radius: 3px;
  border: 1px solid ${(p) => (p.active ? defaultTheme.colors.blue : 'none')};
`
interface SearchInputProps {
  disabled: boolean
}

const SearchContainer = styled.div<SearchInputProps>`
  width: 100%;
  height: 48px;
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  border: 1px solid ${defaultTheme.colors.border};
  border-radius: 3px;
  background-color: ${(p) =>
    p.disabled
      ? defaultTheme.colors.disabledBackground
      : defaultTheme.colors.white};

  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
  }

  @media screen and (max-width: ${breakpoints.tabletXLarge}px) {
    margin: 0px;
    font-weight: bold;
  }
`

const SearchInput = styled.input<SearchInputProps>`
  width: 80%;
  height: 100%;
  border: none;
  padding-left: 1rem;
  display: block;
  font-family: Roboto;
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`
const Arrow = styled.div`
  position: absolute;
  right: 16px;
  font-size: 20px;
  cursor: 'pointer';
`
